import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import XMSyncPanel from "@/components/xm/XMSyncPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plugs, PlugsConnected, Plus, Trash, PencilSimple, ArrowsClockwise,
  CheckCircle, XCircle, Clock, Article, Lightning, UsersThree, Wallet,
} from "@phosphor-icons/react";

const BROKER_TYPES = [
  { value: "mt5", label: "MetaTrader 5 (MT5)" },
  { value: "mt4", label: "MetaTrader 4 (MT4)" },
  { value: "ctrader", label: "cTrader Open API" },
  { value: "match_trader", label: "Match-Trader API" },
  { value: "tradelocker", label: "TradeLocker API" },
  { value: "custom_rest", label: "Custom REST API" },
];

const AUTH_METHODS = [
  { value: "bearer", label: "Bearer Token (Authorization header)" },
  { value: "api_key_header", label: "Custom API key header (e.g. X-API-Key)" },
  { value: "basic", label: "HTTP Basic (key:secret)" },
  { value: "query_param", label: "Query string (?api_key=...)" },
  { value: "none", label: "No authentication" },
];

const MAPPING_FIELDS = [
  { key: "account_number", label: "Account number", required: true, hint: "e.g. login, account, accountId" },
  { key: "trade_ticket", label: "Trade ticket / order ID", required: true, hint: "e.g. ticket, orderId, tradeId" },
  { key: "symbol", label: "Symbol", required: false, hint: "e.g. symbol, instrument" },
  { key: "lot_volume", label: "Lot volume", required: true, hint: "e.g. volume, lots, size" },
  { key: "open_time", label: "Open time", required: false, hint: "e.g. openTime, opened_at" },
  { key: "close_time", label: "Close time", required: true, hint: "e.g. closeTime, closed_at" },
  { key: "profit", label: "Profit", required: false, hint: "e.g. profit, pnl" },
  { key: "trade_status", label: "Trade status", required: true, hint: "Must contain string 'closed' for valid trades" },
];

const emptyForm = () => ({
  name: "",
  broker_type: "custom_rest",
  integration_mode: "webhook",
  api_endpoint: "",
  api_key: "",
  api_secret: "",
  auth_method: "bearer",
  auth_header_name: "X-API-Key",
  account_filter: "all",
  field_mapping: MAPPING_FIELDS.reduce((a, f) => ({ ...a, [f.key]: "" }), {}),
  // Client CRM sync
  clients_endpoint: "",
  client_field_mapping: { full_name: "", email: "", phone: "", broker_account_number: "", broker_server: "", account_type: "", deposit_amount: "", telegram_username: "" },
  auto_register_clients: false,
  // Account balance / equity sync
  accounts_endpoint: "",
  account_field_mapping: { account_number: "", balance: "", equity: "", margin: "", free_margin: "", leverage: "", currency: "", account_active: "" },
  // Schedule
  sync_interval_minutes: 30,
  auto_sync_enabled: false,
  enabled: true,
  notes: "",
});

export default function BrokerIntegrations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | {id?, ...}
  const [logs, setLogs] = useState(null); // {integration_id, rows}
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/broker-integrations");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => setForm(emptyForm());
  const startEdit = (row) => setForm({
    ...emptyForm(),
    ...row,
    api_key: "",
    api_secret: "",
    field_mapping: row.field_mapping || emptyForm().field_mapping,
  });

  const submitForm = async () => {
    if (!form?.name) {
      toast.error("Name is required");
      return;
    }
    if (form?.integration_mode !== "webhook" && !form?.api_endpoint) {
      toast.error("API endpoint is required for Pull mode");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      // strip empty key/secret on edit to avoid overwriting
      if (form.id) {
        if (!payload.api_key) delete payload.api_key;
        if (!payload.api_secret) delete payload.api_secret;
        await api.put(`/broker-integrations/${form.id}`, payload);
        toast.success("Integration updated");
      } else {
        await api.post("/broker-integrations", payload);
        toast.success("Integration created");
      }
      setForm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete integration "${row.name}"?`)) return;
    try { await api.delete(`/broker-integrations/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const testConn = async (row) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/broker-integrations/${row.id}/test`);
      setTestResult({ row, ...data });
      if (data.ok) toast.success(`Connected · ${data.elapsed_ms}ms`);
      else toast.error(`Test failed: ${data.error || data.status_code}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const runSync = async (row) => {
    if (!window.confirm(`Run sync for "${row.name}" now? This will fetch trades and update participant totals.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/broker-integrations/${row.id}/sync`);
      toast.success(`Sync OK · ${data.matched_rows} valid trades · ${data.participants_updated} participants`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const syncClients = async (row) => {
    if (!row.clients_endpoint) { toast.error("Clients endpoint not configured"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/broker-integrations/${row.id}/sync-clients`);
      toast.success(`${data.created} created · ${data.skipped} skipped`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const syncBalances = async (row) => {
    if (!row.accounts_endpoint) { toast.error("Accounts endpoint not configured"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/broker-integrations/${row.id}/sync-balances`);
      toast.success(`${data.updated} accounts updated`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const openLogs = async (row) => {
    try {
      const { data } = await api.get(`/broker-integrations/${row.id}/sync-logs`);
      setLogs({ row, rows: data });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <PageHeader
        title="Broker Integrations"
        subtitle="Connect to your broker's REST API for live trade data. Configure once, sync automatically — no more CSV uploads."
        breadcrumbs="Integrations / Broker"
        testId="integrations-page"
        actions={
          <button data-testid="integration-create-btn" onClick={startCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md">
            <Plus size={14} /> New Integration
          </button>
        }
      />

      <XMSyncPanel />

      {/* Info banner */}
      {rows.length === 0 && !loading && (
        <div className="mb-6 p-5 rounded-lg border border-blue-500/20 bg-blue-500/5">
          <div className="flex items-start gap-3">
            <Lightning size={24} weight="duotone" className="text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-display text-base font-bold text-white mb-1">No broker integration yet</p>
              <p className="text-sm text-gray-400 leading-relaxed">
                Add your first broker to receive automatic trade data from MT5, cTrader, Match-Trader, or any REST API.
                You will need: <span className="text-blue-300 font-mono">API endpoint URL</span>, <span className="text-blue-300 font-mono">API key</span>, and
                how your broker formats the JSON response (field names for account, ticket, volume, etc.).
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-[#111827] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#1F2937]/50 text-left text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3">Integration</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Endpoint</th>
                <th className="px-4 py-3">Webhook URL</th>
                <th className="px-4 py-3">Last Sync</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="p-8 text-center text-gray-500">Loading…</td></tr> :
                rows.length === 0 ? <tr><td colSpan={7} className="p-12 text-center text-gray-500">No integrations yet. Click "New Integration" to start.</td></tr> :
                rows.map((r) => (
                  <tr key={r.id} data-testid={`integration-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${r.enabled ? "bg-blue-500/10 text-blue-400" : "bg-gray-500/10 text-gray-500"}`}>
                          {r.enabled ? <PlugsConnected size={16} weight="duotone" /> : <Plugs size={16} weight="duotone" />}
                        </div>
                        <div>
                          <p className="text-white font-medium">{r.name}</p>
                          <p className="text-[10px] font-mono text-gray-500">{r.sync_interval_minutes}min interval · {r.enabled ? "enabled" : "disabled"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-300 uppercase font-mono">{r.broker_type}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-400 max-w-xs truncate" title={r.api_endpoint}>{r.api_endpoint}</td>
                    <td className="px-4 py-3 text-xs">
                      {r.webhook_token ? (
                        <button
                          data-testid={`copy-webhook-${r.id}`}
                          onClick={() => {
                            const url = `${window.location.origin}/api/webhooks/broker/${r.webhook_token}`;
                            navigator.clipboard.writeText(url);
                            toast.success("Webhook URL copied");
                          }}
                          className="font-mono text-[10px] px-2 py-1 rounded-md bg-blue-500/10 text-blue-300 border border-blue-500/20 hover:bg-blue-500/20"
                          title={`${window.location.origin}/api/webhooks/broker/${r.webhook_token}`}
                        >
                          Copy Webhook URL
                        </button>
                      ) : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-400">{r.last_sync_at ? r.last_sync_at.slice(0, 16).replace("T", " ") : "—"}</td>
                    <td className="px-4 py-3">
                      <StatusPill status={r.last_sync_status} message={r.last_sync_message} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button data-testid={`integration-test-${r.id}`} onClick={() => testConn(r)} disabled={busy} className="p-1.5 rounded-md hover:bg-blue-500/10 text-blue-400" title="Test connection">
                          <Lightning size={15} />
                        </button>
                        <button data-testid={`integration-sync-${r.id}`} onClick={() => runSync(r)} disabled={busy || !r.enabled} className="p-1.5 rounded-md hover:bg-emerald-500/10 text-emerald-400 disabled:opacity-30" title="Sync trades now">
                          <ArrowsClockwise size={15} />
                        </button>
                        <button data-testid={`integration-sync-clients-${r.id}`} onClick={() => syncClients(r)} disabled={busy || !r.clients_endpoint} className="p-1.5 rounded-md hover:bg-blue-500/10 text-blue-400 disabled:opacity-30" title="Sync clients from CRM">
                          <UsersThree size={15} />
                        </button>
                        <button data-testid={`integration-sync-balances-${r.id}`} onClick={() => syncBalances(r)} disabled={busy || !r.accounts_endpoint} className="p-1.5 rounded-md hover:bg-orange-500/10 text-orange-400 disabled:opacity-30" title="Sync balance/equity">
                          <Wallet size={15} />
                        </button>
                        <button data-testid={`integration-logs-${r.id}`} onClick={() => openLogs(r)} className="p-1.5 rounded-md hover:bg-white/5 text-gray-400" title="View sync logs">
                          <Article size={15} />
                        </button>
                        <button data-testid={`integration-edit-${r.id}`} onClick={() => startEdit(r)} className="p-1.5 rounded-md hover:bg-white/5 text-gray-400" title="Edit">
                          <PencilSimple size={15} />
                        </button>
                        <button data-testid={`integration-delete-${r.id}`} onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400" title="Delete">
                          <Trash size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-white max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{form?.id ? "Edit Integration" : "New Broker Integration"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Name" required>
                  <input data-testid="integration-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. MBE Live MT5" className="input" />
                </Field>
                <Field label="Broker type">
                  <select data-testid="integration-type" value={form.broker_type} onChange={(e) => setForm({ ...form, broker_type: e.target.value })} className="input">
                    {BROKER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Integration mode" hint="How data flows between broker and this tracker">
                <select data-testid="integration-mode" value={form.integration_mode || "webhook"} onChange={(e) => setForm({ ...form, integration_mode: e.target.value })} className="input">
                  <option value="webhook">Webhook (broker pushes events to our URL — recommended)</option>
                  <option value="pull">Pull (we fetch from broker API on schedule)</option>
                </select>
              </Field>

              {form.integration_mode !== "webhook" && (
                <Field label="API endpoint URL" required>
                  <input data-testid="integration-endpoint" value={form.api_endpoint} onChange={(e) => setForm({ ...form, api_endpoint: e.target.value })} placeholder="https://api.broker.com/v1/trades/closed" className="input font-mono text-xs" />
                </Field>
              )}

              {form.integration_mode === "webhook" && (
                <div className="p-3 rounded-md bg-blue-500/5 border border-blue-500/20 text-xs text-blue-200">
                  Webhook mode: after saving, copy the generated <span className="font-mono">Webhook URL</span> from the table and share it with your broker. Broker sends events to that URL. No API endpoint or credentials needed on our side.
                </div>
              )}

              {form.integration_mode !== "webhook" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Auth method">
                    <select data-testid="integration-auth-method" value={form.auth_method} onChange={(e) => setForm({ ...form, auth_method: e.target.value })} className="input">
                      {AUTH_METHODS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                  </Field>
                  {form.auth_method === "api_key_header" && (
                    <Field label="Header name">
                      <input data-testid="integration-header-name" value={form.auth_header_name} onChange={(e) => setForm({ ...form, auth_header_name: e.target.value })} placeholder="X-API-Key" className="input" />
                    </Field>
                  )}
                </div>
              )}

              {form.integration_mode !== "webhook" && form.auth_method !== "none" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label={form.id ? "API key (leave blank to keep)" : "API key"}>
                    <input data-testid="integration-api-key" type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder={form.id ? "•••• keep existing" : "Paste API key"} className="input font-mono" />
                  </Field>
                  {form.auth_method === "basic" && (
                    <Field label={form.id ? "Secret (leave blank to keep)" : "Secret"}>
                      <input data-testid="integration-api-secret" type="password" value={form.api_secret} onChange={(e) => setForm({ ...form, api_secret: e.target.value })} className="input font-mono" />
                    </Field>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Account filter" hint="'all' or comma-separated account numbers">
                  <input data-testid="integration-account-filter" value={form.account_filter} onChange={(e) => setForm({ ...form, account_filter: e.target.value })} placeholder="all  or  1234567,8901234" className="input" />
                </Field>
                <Field label="Auto-sync interval (minutes)">
                  <input data-testid="integration-interval" type="number" min="5" value={form.sync_interval_minutes} onChange={(e) => setForm({ ...form, sync_interval_minutes: parseInt(e.target.value || "30") })} className="input" />
                </Field>
              </div>

              <div className="pt-3 border-t border-white/10">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Field mapping</p>
                <p className="text-xs text-gray-500 mb-3">Map the field names returned by your broker's JSON response to our system fields. Required fields must be provided.</p>
                <div className="space-y-2">
                  {MAPPING_FIELDS.map((f) => (
                    <div key={f.key} className="flex items-center gap-3">
                      <div className="w-44 flex-shrink-0">
                        <p className="text-sm text-white">{f.label} {f.required && <span className="text-orange-400">*</span>}</p>
                        <p className="text-[10px] font-mono text-gray-500">{f.hint}</p>
                      </div>
                      <input
                        data-testid={`integration-map-${f.key}`}
                        value={form.field_mapping[f.key] || ""}
                        onChange={(e) => setForm({ ...form, field_mapping: { ...form.field_mapping, [f.key]: e.target.value } })}
                        placeholder={f.required ? "broker JSON field name" : "(optional)"}
                        className="input flex-1 font-mono text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-3 border-t border-white/10">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-2">
                  <UsersThree size={12} /> Client CRM auto-register
                </p>
                <p className="text-xs text-gray-500 mb-3">Optional. Provide an endpoint that returns the client list from the broker CRM. The system will auto-create participants (status: pending) for new clients.</p>
                <Field label="Clients endpoint URL">
                  <input data-testid="integration-clients-endpoint" value={form.clients_endpoint || ""} onChange={(e) => setForm({ ...form, clients_endpoint: e.target.value })} placeholder="https://api.broker.com/v1/clients" className="input font-mono text-xs" />
                </Field>
                {form.clients_endpoint && (
                  <>
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer mt-3 mb-3">
                      <input data-testid="integration-auto-register" type="checkbox" checked={form.auto_register_clients} onChange={(e) => setForm({ ...form, auto_register_clients: e.target.checked })} />
                      Auto-register new clients on every scheduled sync
                    </label>
                    <div className="space-y-2">
                      {[
                        ["full_name", "Full name *"],
                        ["email", "Email *"],
                        ["broker_account_number", "Account number *"],
                        ["phone", "Phone"],
                        ["broker_server", "Broker server"],
                        ["account_type", "Account type"],
                        ["deposit_amount", "Deposit amount"],
                        ["telegram_username", "Telegram"],
                      ].map(([k, label]) => (
                        <div key={k} className="flex items-center gap-3">
                          <div className="w-44 flex-shrink-0">
                            <p className="text-sm text-white">{label}</p>
                          </div>
                          <input
                            data-testid={`integration-client-map-${k}`}
                            value={form.client_field_mapping?.[k] || ""}
                            onChange={(e) => setForm({ ...form, client_field_mapping: { ...form.client_field_mapping, [k]: e.target.value } })}
                            placeholder="broker JSON field name"
                            className="input flex-1 font-mono text-xs"
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="pt-3 border-t border-white/10">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-1 flex items-center gap-2">
                  <Wallet size={12} /> Balance / equity sync
                </p>
                <p className="text-xs text-gray-500 mb-3">Optional. Endpoint that returns account balance & equity. Values are shown on the participant detail page.</p>
                <Field label="Accounts endpoint URL">
                  <input data-testid="integration-accounts-endpoint" value={form.accounts_endpoint || ""} onChange={(e) => setForm({ ...form, accounts_endpoint: e.target.value })} placeholder="https://api.broker.com/v1/accounts" className="input font-mono text-xs" />
                </Field>
                {form.accounts_endpoint && (
                  <div className="space-y-2 mt-3">
                    {[
                      ["account_number", "Account number *"],
                      ["balance", "Balance"],
                      ["equity", "Equity"],
                      ["margin", "Margin used"],
                      ["free_margin", "Free margin"],
                      ["leverage", "Leverage"],
                      ["currency", "Currency"],
                      ["account_active", "Account active flag"],
                    ].map(([k, label]) => (
                      <div key={k} className="flex items-center gap-3">
                        <div className="w-44 flex-shrink-0">
                          <p className="text-sm text-white">{label}</p>
                        </div>
                        <input
                          data-testid={`integration-account-map-${k}`}
                          value={form.account_field_mapping?.[k] || ""}
                          onChange={(e) => setForm({ ...form, account_field_mapping: { ...form.account_field_mapping, [k]: e.target.value } })}
                          placeholder="broker JSON field name"
                          className="input flex-1 font-mono text-xs"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input data-testid="integration-enabled" type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
                  Enabled (allow sync)
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input data-testid="integration-auto-sync" type="checkbox" checked={form.auto_sync_enabled} onChange={(e) => setForm({ ...form, auto_sync_enabled: e.target.checked })} />
                  Auto-sync ({form.sync_interval_minutes} min interval)
                </label>
              </div>

              <Field label="Notes">
                <textarea data-testid="integration-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="input" placeholder="Internal notes about this integration" />
              </Field>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setForm(null)} className="px-4 py-2 text-sm text-gray-400">Cancel</button>
            <button data-testid="integration-submit" onClick={submitForm} disabled={busy} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50">
              {form?.id ? "Save changes" : "Create Integration"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test result dialog */}
      <Dialog open={!!testResult} onOpenChange={(o) => !o && setTestResult(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              {testResult?.ok ? <CheckCircle size={20} className="text-emerald-400" weight="fill" /> : <XCircle size={20} className="text-red-400" weight="fill" />}
              Test result · {testResult?.row?.name}
            </DialogTitle>
          </DialogHeader>
          {testResult && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Status code" value={testResult.status_code || "—"} />
                <Stat label="Elapsed" value={`${testResult.elapsed_ms || 0} ms`} />
              </div>
              {testResult.error && (
                <div className="p-3 rounded-md bg-red-500/10 border border-red-500/30 text-red-200 text-xs font-mono">{testResult.error}</div>
              )}
              {testResult.detected_fields?.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Detected fields in response</p>
                  <div className="flex flex-wrap gap-1.5">
                    {testResult.detected_fields.map((f) => (
                      <span key={f} className="text-xs px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-300 border border-blue-500/20 font-mono">{f}</span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Tip: copy these field names into the "Field mapping" form so the sync can read each trade correctly.</p>
                </div>
              )}
              {testResult.body_preview && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Response preview</p>
                  <pre className="text-xs font-mono bg-[#0B0F19] border border-white/10 rounded-md p-3 max-h-48 overflow-auto text-gray-300">{testResult.body_preview}</pre>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setTestResult(null)} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync logs dialog */}
      <Dialog open={!!logs} onOpenChange={(o) => !o && setLogs(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-white max-w-4xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Sync logs · {logs?.row?.name}</DialogTitle>
          </DialogHeader>
          {logs?.rows?.length === 0 && <p className="text-sm text-gray-400">No sync logs yet.</p>}
          <div className="space-y-2">
            {logs?.rows?.map((l) => (
              <div key={l.id} className={`p-3 rounded-md border ${l.status === "success" ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/20 bg-red-500/5"}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-mono text-xs text-gray-400">{(l.started_at || "").slice(0, 19).replace("T", " ")}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${l.status === "success" ? "bg-emerald-500/10 text-emerald-300" : "bg-red-500/10 text-red-300"}`}>
                    {l.status} · {l.elapsed_ms}ms
                  </span>
                </div>
                {l.status === "success" ? (
                  <p className="text-xs text-gray-300">
                    <span className="font-mono">{l.rows_fetched}</span> rows fetched ·
                    <span className="font-mono text-emerald-400"> {l.matched_rows}</span> valid ·
                    <span className="font-mono text-gray-400"> {l.unmatched_rows}</span> unmatched ·
                    <span className="font-mono text-gray-400"> {l.duplicate_rows}</span> dup ·
                    <span className="font-mono text-red-400"> {l.invalid_rows}</span> invalid ·
                    <span className="font-mono text-blue-300"> {l.total_valid_lot_imported?.toFixed?.(2)}</span> lot ·
                    <span className="font-mono text-orange-300"> {l.milestones_triggered}</span> milestones
                  </p>
                ) : (
                  <p className="text-xs font-mono text-red-300">{l.error}</p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <style>{`.input{width:100%;background:#0B0F19;border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:.55rem .85rem;color:#fff;font-size:.875rem;outline:none;transition:.15s}.input:focus{border-color:#2563EB;box-shadow:0 0 0 1px #2563EB}`}</style>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wider">
        {label} {required && <span className="text-orange-400">*</span>}
        {hint && <span className="ml-2 normal-case tracking-normal text-gray-500 text-[10px]">— {hint}</span>}
      </span>
      {children}
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#0B0F19] border border-white/5 rounded-md p-3">
      <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
      <p className="font-mono text-lg font-bold text-white mt-0.5">{value}</p>
    </div>
  );
}

function StatusPill({ status, message }) {
  const map = {
    success: { cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", icon: <CheckCircle size={12} weight="fill" /> },
    failed: { cls: "bg-red-500/10 text-red-300 border-red-500/30", icon: <XCircle size={12} weight="fill" /> },
    never: { cls: "bg-gray-500/10 text-gray-400 border-gray-500/30", icon: <Clock size={12} /> },
  };
  const s = map[status] || map.never;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${s.cls}`} title={message || ""}>
      {s.icon} {status || "never"}
    </span>
  );
}
